import { useLazyQuery } from '@apollo/client/react';
import { BlobServiceClient } from '@azure/storage-blob';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { ORGANISATION_TEMPLATE_LIBRARY_UPLOAD_SAS } from '../../graphql/templateLibrary';
import {
  PrintFontWeight,
  PrintTemplateAssetKind,
  useOrganisationPrintFontAttachMutation,
  useOrganisationTemplateLibrarySetLogoMutation,
  usePrintTemplateAttachAssetMutation,
  type OrganisationTemplateLibraryUploadSasQuery,
  type OrganisationTemplateLibraryUploadSasQueryVariables,
} from '../../types/graphqlTypes';

const extensionOf = (file: File, fallback: string): string =>
  (file.name.split('.').pop() ?? fallback)
    .replace(/[^a-z0-9]/gi, '')
    .slice(0, 8)
    .toLowerCase() || fallback;

/**
 * Uploads for print templates, straight to the organisation's container with
 * a write SAS, then recorded on the server (which checks the file's type from
 * its bytes): template images under `print-templates/<templateId>/`, the
 * brand logo and fonts under `print-brand/`.
 */
export function useTemplateUploads(organisationId: string) {
  const [requestSas] = useLazyQuery<OrganisationTemplateLibraryUploadSasQuery, OrganisationTemplateLibraryUploadSasQueryVariables>(ORGANISATION_TEMPLATE_LIBRARY_UPLOAD_SAS, { fetchPolicy: 'network-only' });
  const [attachAsset] = usePrintTemplateAttachAssetMutation({ refetchQueries: ['printTemplate'] });
  const [setLogo] = useOrganisationTemplateLibrarySetLogoMutation({ refetchQueries: ['organisationTemplateLibrary', 'projectTemplateLibrary'] });
  const [attachFont] = useOrganisationPrintFontAttachMutation({ refetchQueries: ['organisationTemplateLibrary'] });

  const upload = useCallback(
    async (blobName: string, file: File): Promise<void> => {
      const sas = (await requestSas({ variables: { organisationId } })).data?.organisationTemplateLibraryUploadSAS;
      if (!sas) throw new Error('no upload access');
      await new BlobServiceClient(sas)
        .getContainerClient('')
        .getBlockBlobClient(blobName)
        .uploadData(file, { blobHTTPHeaders: { blobContentType: file.type || 'application/octet-stream' } });
    },
    [requestSas, organisationId],
  );

  const uploadTemplateImage = useCallback(
    async (templateId: string, file: File): Promise<{ id: string; url: string }> => {
      const blobName = `print-templates/${templateId}/${uuid()}.${extensionOf(file, 'png')}`;
      await upload(blobName, file);
      const result = await attachAsset({ variables: { organisationId, templateId, kind: PrintTemplateAssetKind.Image, blobName, originalName: file.name } });
      const asset = result.data?.printTemplateAttachAsset;
      if (!asset) throw new Error('image not recorded');
      return { id: asset.id, url: asset.url };
    },
    [upload, attachAsset, organisationId],
  );

  /** Uploads and sets the brand logo; resolves to its (read) URL. */
  const uploadLogo = useCallback(
    async (file: File): Promise<string | null> => {
      const blobName = `print-brand/${uuid()}.${extensionOf(file, 'png')}`;
      await upload(blobName, file);
      const result = await setLogo({ variables: { organisationId, blobName, originalName: file.name } });
      return result.data?.organisationTemplateLibrarySetLogo.logo?.url ?? null;
    },
    [upload, setLogo, organisationId],
  );

  const removeLogo = useCallback(async () => {
    await setLogo({ variables: { organisationId, blobName: null, originalName: null } });
  }, [setLogo, organisationId]);

  const uploadFont = useCallback(
    async (file: File, family: string, weight: PrintFontWeight, italic: boolean): Promise<void> => {
      const blobName = `print-brand/${uuid()}.${extensionOf(file, 'ttf')}`;
      await upload(blobName, file);
      await attachFont({ variables: { organisationId, blobName, originalName: file.name, family, weight, italic } });
    },
    [upload, attachFont, organisationId],
  );

  return { uploadTemplateImage, uploadLogo, removeLogo, uploadFont };
}
